<template>
    <div class="filters">
        <div class="filters_content">
            <div class="filters_main">
                <label class="filters_label">{{ label }}</label>

                <!-- Filters Row -->
                <div v-if="showSearch || filters.length > 0" class="filters_row">
                    <sw-input
                        v-if="showSearch"
                        :model-value="searchValue"
                        @update:model-value="updateSearch"
                        :label="searchPlaceholder"
                        icon="search"
                        class="filters_search"
                    />
                    <sw-select
                        v-for="filter in filters"
                        :key="filter.key"
                        :model-value="filterValues[filter.key]"
                        @update:model-value="updateFilter(filter.key, $event)"
                        :label="filter.label"
                        :options="filter.options"
                        class="filters_select"
                    />
                </div>
            </div>

            <!-- Clear Filters Button -->
            <div class="filters_actions" v-if="showClearButton">
                <sw-button
                    name="Limpar Filtros"
                    hover-color="blue"
                    icon="cross"
                    @click="clearAllFilters"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';

    const props = defineProps({
        label: {
            type: String,
            default: 'Filtrar por:'
        },
        showSearch: {
            type: Boolean,
            default: true
        },
        searchValue: {
            type: String,
            default: ''
        },
        searchPlaceholder: {
            type: String,
            default: 'Buscar...'
        },
        filters: {
            type: Array,
            default: () => []
            // Formato: [{ key: 'status', label: 'Status', options: [...] }]
        },
        filterValues: {
            type: Object,
            default: () => ({})
            // Formato: { status: 'active', category: 'work' }
        }
    });

    const emit = defineEmits(['update:search', 'update:filter', 'clear-filters']);

    // Computed
    const showClearButton = computed(() => {
        const hasSearch = props.searchValue && props.searchValue.trim() !== '';
        const hasFilters = Object.values(props.filterValues).some(value =>
            value !== '' && value !== null && value !== undefined
        );
        return hasSearch || hasFilters;
    });

    // Methods
    const updateSearch = (value) => {
        emit('update:search', value);
    };

    const updateFilter = (key, value) => {
        emit('update:filter', { key, value });
    };

    const clearAllFilters = () => {
        emit('clear-filters');
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    .filters {
        margin-bottom: 2em;

        .filters_content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1.5em;
        }

        .filters_main {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 1em;

            .filters_label {
                font-size: 0.875em;
                font-weight: 600;
                color: var(--textPrimary);
                margin-bottom: 0.5em;
            }

            .filters_searchRow {
                display: flex;
                width: 100%;

                .filters_search {
                    flex: 1;
                    max-width: 400px;
                }
            }

            .filters_selectsRow {
                display: flex;
                gap: 1.25em;
                align-items: start;
                flex-wrap: wrap;

                .filters_select {
                    flex: 1;
                    min-width: 180px;
                }
            }

            // Manter compatibilidade com o CSS antigo
            .filters_row {
                display: flex;
                gap: 1.25em;
                align-items: start;

                .filters_search {
                    flex: 2;
                    min-width: 280px;
                }

                .filters_select {
                    flex: 1;
                    min-width: 160px;
                }
            }
        }

        .filters_actions {
            display: flex;
            align-items: center;
            gap: 1em;
        }
    }

    @include screen_medias(s) {
        .filters {
            .filters_content {
                flex-direction: column;
                align-items: stretch;
                gap: 1.25em;
            }

            .filters_main {
                .filters_searchRow {
                    .filters_search {
                        max-width: none;
                    }
                }

                .filters_selectsRow {
                    flex-direction: column;
                    gap: 1em;

                    .filters_select {
                        min-width: 0;
                        flex: none;
                    }
                }

                // Manter compatibilidade com o CSS antigo
                .filters_row {
                    flex-direction: column;
                    gap: 1em;

                    .filters_search,
                    .filters_select {
                        min-width: 0;
                    }
                }
            }
        }
    }
</style>